import { useRouter } from 'next/router';
import styles from './BroadcasterPlaylistPlayer.module.scss';
import ShareIcon from '@/icons/share.svg';
import { ToastStatus, useToast } from '@/dls/Toast/Toast';
import useTranslation from 'next-translate/useTranslation';
import useSWRImmutable from 'swr/immutable';
import { getPlaylistVideosById } from '@/api';
import { timeAgo, formatDuration } from '@/utils/common';

const BroadcasterPlaylistPlayer = () => {
  const router = useRouter();
  const { id,  videoId} = router.query;
  const toast = useToast();
  const { t } = useTranslation();

  // Fetch all videos for the sidebar
  // const {
  //   data: getVideosData,
  //   error: videosError,
  // } = useSWRImmutable('videos', async () => {
  //   const res = await getVideos();
  //   return res.videos;
  // });

  // Fetch single video by id (wait for id to be defined)
  const {
    data: playlistData,
    error: videoError,
  } = useSWRImmutable(
    id ? `playlists/${id}/videos` : null,
    async () => {
      const res = await getPlaylistVideosById(id);
      return {videos: res?.videos, videoData: res.videos.find(v => v.id == videoId) || res.videos[0]};
    }
  );

  const handleShareClick = () => {
    if (typeof window !== 'undefined') {
      const url = window.location.href;
      navigator.clipboard.writeText(url).then(() => {
        toast(t('common:shared'), { status: ToastStatus.Success });
      }).catch(err => {
        console.error('Failed to copy:', err);
      });
    }
  };

const handleVideoClick = (video: any) => {
  playlistData.videoData= video; // Update the current video data
  router.push(
    {
      pathname: router.pathname, // stays on the same page
      query: { id: id, videoId: video.id }, // pass video id as well
    },
    undefined,
    { shallow: false } // ensures SWR runs again
  );
};

  const handleVideoEnded = () => {
    // Get the next video from the list
    if (playlistData && playlistData.videoData) {
      const currentIndex = playlistData.videos.findIndex((v: any) => v.id === playlistData.videoData.id);
      const nextVideo = playlistData.videos[currentIndex + 1];
      if (nextVideo) {
        handleVideoClick(nextVideo);
      }
    }
  };
  // Main video loading
  if (!playlistData?.videoData && !videoError) {
    return <div className={styles.loading}>Loading video...</div>;
  }

  if (videoError) {
    return <div className={styles.loading}>Failed to load video</div>;
  }

  if (!playlistData?.videoData) {
    return <div className={styles.loading}>No video found</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        <div className={styles.videoWrapper}>
          <video
            key={playlistData?.videoData.id}
            className={styles.video}
            controls
            controlsList="nodownload" // Prevents downloading
            // poster={playlistData?.videoData?.thumbnail}
            autoPlay
            onEnded={handleVideoEnded}
          >
            <source src={playlistData?.videoData?.url} type="video/mp4" />
            Your browser does not support the video tag.
          </video>

          <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "1rem" }}>
            <h2 className={styles.title}>{playlistData?.videoData?.title}</h2>
            <button className={styles.shareButton} onClick={handleShareClick}>
              <ShareIcon />
              <span>Share</span>
            </button>
          </div>

          <h4>{playlistData?.videoData?.viewCount}&nbsp;Views • {timeAgo(playlistData?.videoData.createdAt)}</h4>
        </div>
      </div>

      {/* Sidebar */}
      <div className={styles.sidebar}>
        {playlistData?.videos?.map((video) => (
          <div
            key={video.id}
            className={styles.videoSuggestions}
            onClick={() => handleVideoClick(video)}
          >
            <div style={{ display: "flex" }}>
              <div className={styles.thumbnailWrapper}>
                <video className={styles.videoThumb} poster={video.thumbnail}>
                  <source src={video.url} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                <span className={styles.timestamp}>{formatDuration(video.duration)}</span>
              </div>
              <div className={styles.videoDetails}>
                <h3 className={styles.videoTitle}>{video.title}</h3>
                <p className={styles.metaInfo}>
                  {video.viewsCount} &nbsp;Views • {timeAgo(video.createdAt)}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BroadcasterPlaylistPlayer;
